"use client"

import { useMemo } from "react"
import { cn } from "@/lib/utils"
import { convertToLocale } from "@lib/util/money"
import { calculateRentalTotal, formatDate, getUnitLabel, addRentalPeriod } from "@lib/util/rental"
import type { RentalUnit } from "@/types/rental"
import { useTranslations, useLocale } from "next-intl"

interface RentalPriceDisplayProps {
  rentalPrice: number
  depositAmount: number
  duration: number
  quantity: number
  rentalUnit: RentalUnit
  currencyCode: string
  startDate?: Date | null
  className?: string
}

export default function RentalPriceDisplay({
  rentalPrice,
  depositAmount,
  duration,
  quantity,
  rentalUnit,
  currencyCode,
  startDate,
  className,
}: RentalPriceDisplayProps) {
  const t = useTranslations('product.rental')
  const locale = useLocale()

  const localeMap: Record<string, string> = {
    ru: 'ru-RU',
    en: 'en-US',
    cs: 'cs-CZ',
    uk: 'uk-UA',
  }
  const intlLocale = localeMap[locale] || 'en-US'

  // Calculate totals
  const { rental, deposit, total } = useMemo(
    () => calculateRentalTotal(rentalPrice, depositAmount, duration, quantity),
    [rentalPrice, depositAmount, duration, quantity]
  )

  // Format prices
  const formatPrice = (amount: number) => convertToLocale({
    amount,
    currency_code: currencyCode,
  })

  // Calculate end date
  const endDate = useMemo(() => {
    if (!startDate) return null
    return addRentalPeriod(startDate, duration, rentalUnit)
  }, [startDate, duration, rentalUnit])

  const unitLabel = getUnitLabel(rentalUnit, 1, locale)

  return (
    <div className={cn("space-y-3", className)}>
      {/* Rental period dates */}
      {startDate && endDate && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-foreground-secondary">{t('rentalPeriod')}</span>
          <span className="font-medium">
            {formatDate(startDate, locale)} — {formatDate(endDate, locale)}
          </span>
        </div>
      )}

      {/* Price breakdown */}
      <div className="space-y-2 py-3 border-y border-border">
        {/* Rental cost */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-foreground-secondary">
            {t('pricePerPeriod', { unit: unitLabel })} × {duration}
          </span>
          <span>
            {formatPrice(rentalPrice)} × {duration} = {formatPrice(rental)}
          </span>
        </div>

        {/* Quantity (if > 1) */}
        {quantity > 1 && (
          <div className="flex items-center justify-between text-sm text-foreground-secondary">
            <span>{t('quantity')}</span>
            <span>× {quantity}</span>
          </div>
        )}

        {/* Nothing is held and nothing is frozen: this is the ceiling of what the
            shop may charge if the copy comes back broken or does not come back.
            It is not part of the total, it is not a deposit, and the customer is
            told so in one sentence below rather than by a scary money row. */}
        {depositAmount > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground-secondary">{t('damageLiability')}</span>
            <span>{t('upTo', { amount: formatPrice(deposit) })}</span>
          </div>
        )}
      </div>

      {/* Total — what the card is charged. Any promotion is applied by Medusa in the
          cart, where the adjustment is shown against the line it discounts. */}
      <div className="flex items-center justify-between">
        <span className="text-base font-semibold">{t('total')}</span>
        <span className="text-lg font-bold text-primary">{formatPrice(total)}</span>
      </div>

      {/* Why that line is not a price */}
      {depositAmount > 0 && (
        <p className="text-xs text-foreground-tertiary">
          {t('damageNote')}
        </p>
      )}
    </div>
  )
}
